"use client";

import { useEffect, useRef } from "react";
import "./flowers.css";

/* ─────────────────────────────────────────────────────────────────────────
   Props
───────────────────────────────────────────────────────────────────────── */
interface FlowersSceneProps {
  /** Container width in px (default 400) */
  width?: number;
  /** Container height in px (default 400) */
  height?: number;
  className?:string
}

/* ─────────────────────────────────────────────────────────────────────────
   Small helpers
───────────────────────────────────────────────────────────────────────── */
function FlowerLights() {
  return (
    <>
      {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
        <div key={n} className={`flower__light flower__light--${n}`} />
      ))}
    </>
  );
}

function Flower({ index }: { index: 1 | 2 | 3 }) {
  const lineLeavesCount: Record<1 | 2 | 3, number> = { 1: 6, 2: 4, 3: 4 };
  return (
    <div className={`flower flower--${index}`}>
      <div className={`flower__leafs flower__leafs--${index}`}>
        {[1, 2, 3, 4].map((n) => (
          <div key={n} className={`flower__leaf flower__leaf--${n}`} />
        ))}
        <div className="flower__white-circle" />
        <FlowerLights />
      </div>
      <div className="flower__line">
        {Array.from({ length: lineLeavesCount[index] }, (_, i) => i + 1).map((n) => (
          <div key={n} className={`flower__line__leaf flower__line__leaf--${n}`} />
        ))}
      </div>
    </div>
  );
}

function GrowWrap({ delay, children }: { delay: string; children: React.ReactNode }) {
  return (
    <div className="grow-ans" style={{ ["--d" as string]: delay }}>
      {children}
    </div>
  );
}

function LongG({ variant, delays }: { variant: number; delays: [string, string, string, string] }) {
  return (
    <div className={`long-g long-g--${variant}`}>
      {(["0", "1", "2", "3"] as const).map((n, i) => (
        <GrowWrap key={n} delay={delays[i]}>
          <div className={`leaf leaf--${n}`} />
        </GrowWrap>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Main component
───────────────────────────────────────────────────────────────────────── */
export default function FlowersScene({ className, width = 400, height = 400 }: FlowersSceneProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  /* Pause animations until container is ready, then play */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.classList.add("not-loaded");
    const t = setTimeout(() => el.classList.remove("not-loaded"), 1000);
    return () => clearTimeout(t);
  }, []);

  /*
   * --u = 1% of the smaller dimension (mirrors what `vmin` did for the
   * full-screen version).  All CSS values use `calc(var(--u) * X)` instead
   * of `Xvmin`, so the scene scales with width/height, not the viewport.
   */
  const u = Math.min(width, height) / 100;

  const containerStyle: React.CSSProperties & Record<string, unknown> = {
    width,
    height,
    "--u": `${u}px`,
  };

  return (
    <div ref={containerRef} className={`flowers-scene ${className}`} style={containerStyle}>
      {/* <div className="night" /> */}

      <div className={`flowers ${className}`}>
        <Flower index={1} />
        <Flower index={2} />
        <Flower index={3} />

        <GrowWrap delay="1.2s">
          <div className="flower__g-long">
            <div className="flower__g-long__top" />
            <div className="flower__g-long__bottom" />
          </div>
        </GrowWrap>

        {[1, 2].map((n) => (
          <div key={n} className="growing-grass">
            <div className={`flower__grass flower__grass--${n}`}>
              <div className="flower__grass--top" />
              <div className="flower__grass--bottom" />
              {[1, 2, 3, 4, 5, 6, 7, 8].map((l) => (
                <div key={l} className={`flower__grass__leaf flower__grass__leaf--${l}`} />
              ))}
              {/* <div className="flower__grass__overlay" /> */}
            </div>
          </div>
        ))}

        <GrowWrap delay="2.4s">
          <div className="flower__g-right flower__g-right--1">
            <div className="leaf" />
          </div>
        </GrowWrap>

        <GrowWrap delay="2.8s">
          <div className="flower__g-right flower__g-right--2">
            <div className="leaf" />
          </div>
        </GrowWrap>

        <GrowWrap delay="2.8s">
          <div className="flower__g-front">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <div key={n} className={`flower__g-front__leaf-wrapper flower__g-front__leaf-wrapper--${n}`}>
                <div className="flower__g-front__leaf" />
              </div>
            ))}
            <div className="flower__g-front__line" />
          </div>
        </GrowWrap>

        <GrowWrap delay="3.2s">
          <div className="flower__g-fr">
            <div className="leaf" />
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <div key={n} className={`flower__g-fr__leaf flower__g-fr__leaf--${n}`} />
            ))}
          </div>
        </GrowWrap>

        <LongG variant={0} delays={["3s",   "2.2s", "3.4s", "3.6s"]} />
        <LongG variant={1} delays={["3.6s", "3.8s", "4s",   "4.2s"]} />
        <LongG variant={2} delays={["4s",   "4.2s", "4.4s", "4.6s"]} />
        <LongG variant={3} delays={["4s",   "4.2s", "3s",   "3.6s"]} />
        <LongG variant={4} delays={["4s",   "4.2s", "3s",   "3.6s"]} />
        <LongG variant={5} delays={["4s",   "4.2s", "3s",   "3.6s"]} />
        <LongG variant={6} delays={["4.2s", "4.4s", "4.6s", "4.8s"]} />
        <LongG variant={7} delays={["3s",   "3.2s", "3.5s", "3.6s"]} />
      </div>
    </div>
  );
}